import React from 'react';
import { AlertTriangle, Calendar, Clock } from 'lucide-react';
import { dashboardService } from '../../../services/dashboardService';
import { differenceInDays, parseISO, format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';
import { isForbiddenError } from '../../../services/api';

export interface LowStockItem {
  id: string;
  name: string;
  current: number;
  min: number;
  unit: string;
  itemType?: string;
}

interface ExpiringItem {
  id: string;
  name: string;
  lot: string;
  expiryDate: string;
  quantity: number;
  unit: string;
  daysUntilExpiry: number;
  itemType?: string;
  workOrderId?: string;
}

interface Props {
  onReorder?: (item: LowStockItem) => void;
  onExpiringItemClick?: (item: ExpiringItem) => void;
  showExpiringSoon?: boolean;
  showLowStock?: boolean;
}

export const InventoryAlerts: React.FC<Props> = ({
  onReorder,
  onExpiringItemClick,
  showExpiringSoon = true,
  showLowStock = true,
}) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canReadInventory = can('inventory', 'read');
  const canReorder = can('inventory', 'write') || can('purchase_orders', 'write') || can('work_orders.roasting', 'write');
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['inventory-alerts'],
    queryFn: dashboardService.getInventoryAlerts,
    enabled: canReadInventory,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });
  const isForbidden = !canReadInventory || isForbiddenError(queryError);

  const lowStockItems: LowStockItem[] = React.useMemo(() => {
    return (data?.low_stock ?? []).map(item => ({
      id: item.id,
      name: item.name,
      current: item.stock,
      min: item.itemType === 'finished_good' ? 10 : (item.reorderPoint || 0),
      unit: item.unitOfMeasure,
      itemType: item.itemType
    }));
  }, [data]);

  const expiringItems: ExpiringItem[] = React.useMemo(() => {
    const today = new Date();
    return (data?.expiring_soon ?? [])
      .map(item => {
        const expiryDate = item.expiryDate ? parseISO(item.expiryDate) : new Date();
        return {
          id: item.id,
          name: item.name,
          lot: item.code || '',
          expiryDate: item.expiryDate || '',
          quantity: item.stock,
          unit: item.unitOfMeasure,
          daysUntilExpiry: differenceInDays(expiryDate, today),
          itemType: item.itemType,
          workOrderId: item.workOrderId,
        };
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  }, [data]);

  const getUrgencyIconColor = (days: number) => {
    if (days <= 7) return 'text-red-500';
    if (days <= 30) return 'text-yellow-500';
    return 'text-gray-500';
  };

  if (!showExpiringSoon && !showLowStock || isForbidden) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        {showExpiringSoon && <div className="bg-white shadow rounded-lg p-6 animate-pulse h-[200px]"></div>}
        {showLowStock && <div className="bg-white shadow rounded-lg p-6 animate-pulse h-[200px]"></div>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {showExpiringSoon && (
        <div className="bg-white shadow rounded-lg overflow-hidden flex flex-col">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex items-center gap-2 shrink-0">
            <Calendar className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('dashboard.inventoryAlerts.expiringSoon')}
              <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                {expiringItems.length}
              </span>
            </h3>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <ul className="divide-y divide-gray-200">
              {expiringItems.map((item) => (
                <li key={item.id} className="hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => onExpiringItemClick?.(item)}
                    disabled={!item.workOrderId}
                    className={`flex w-full items-center justify-between px-4 py-4 text-left ${
                      item.workOrderId ? 'cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Clock className={`h-4 w-4 ${getUrgencyIconColor(item.daysUntilExpiry)}`} />
                        <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
                      </div>
                      <div className="mt-1 ml-6 flex flex-col text-sm text-gray-500 sm:flex-row sm:gap-4">
                        <span className="truncate">
                          {item.itemType === 'roasted_bulk'
                            ? t('dashboard.inventoryAlerts.batch')
                            : t('dashboard.inventoryAlerts.lot')}: {item.lot}
                        </span>
                        <span className={`truncate font-medium ${
                          item.daysUntilExpiry <= 7 ? 'text-red-600' :
                          item.daysUntilExpiry <= 30 ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          {t('dashboard.inventoryAlerts.expiry')}: {item.expiryDate ? format(parseISO(item.expiryDate), 'MMM d, yyyy') : t('common.table.na')} ({item.daysUntilExpiry} {t('dashboard.inventoryAlerts.daysSuffix')})
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 text-right text-sm text-gray-500">
                      {item.quantity} {item.unit}
                    </div>
                  </button>
                </li>
              ))}
              {expiringItems.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-gray-500">{t('dashboard.inventoryAlerts.noExpiringItems')}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {showLowStock && (
        <div className="bg-white shadow rounded-lg overflow-hidden flex flex-col">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex items-center gap-2 shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('dashboard.inventoryAlerts.lowStock')}
              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                {lowStockItems.length}
              </span>
            </h3>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <ul className="divide-y divide-gray-200">
              {lowStockItems.map((item) => (
                <li key={item.id} className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="truncate text-sm text-red-500">
                      {item.current} {item.unit} ({t('dashboard.inventoryAlerts.min')}: {item.min} {item.unit})
                    </p>
                  </div>
                  <div>
                    {canReorder && (
                      <button
                        onClick={() => onReorder?.(item)}
                        className={`inline-flex items-center rounded-full border border-transparent px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          (item.itemType === 'finished_good' || item.itemType === 'roasted_bulk')
                            ? 'bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500'
                        }`}
                      >
                        {(item.itemType === 'finished_good' || item.itemType === 'roasted_bulk')
                          ? t('dashboard.inventoryAlerts.restock') 
                          : t('dashboard.inventoryAlerts.reorder')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {lowStockItems.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-gray-500">{t('dashboard.inventoryAlerts.noLowStockItems')}</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
